import { useCallback, useEffect, useState } from "react";
import { fetchPrimary } from "../environments/primary/fetch";

// Approving "stop this World's subscription renewing" with Touch ID
// (docs/world-subscription-cancel-design.md §4). The card sends only its World
// and its own key; the local server reads the exact facts, asks the Mac's
// signer (which shows those words in the Touch ID sheet) and records the
// approval. Declining stays on the ordinary decide lane and needs no Touch ID.

export type TouchIdApproval =
  | { readonly ok: true }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

const APPROVE_TIMEOUT_MS = 200_000;
const STATUS_TIMEOUT_MS = 25_000;
const FAILED = "Neucleos did not accept this approval. Nothing was changed.";
const UNKNOWN_AVAILABILITY = "Touch ID could not be checked on this Mac, so this cannot be approved here.";

export async function approveSubscriptionWithTouchId(
  worldKey: string,
  gateInstanceKey: string,
): Promise<TouchIdApproval> {
  try {
    const response = await fetchPrimary("/api/neucleos/commerce/subscription-approve", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ worldKey, gateInstanceKey }),
      signal: AbortSignal.timeout(APPROVE_TIMEOUT_MS),
    });
    const body = (await response.json().catch(() => null)) as {
      ok?: unknown;
      kind?: unknown;
      reason?: unknown;
    } | null;
    if (response.ok && body?.ok === true) return { ok: true };
    // The server's words are already plain; anything else is one plain sentence.
    const plain = typeof body?.kind === "string" && typeof body?.reason === "string";
    return {
      ok: false,
      kind: plain ? (body!.kind as string) : "refused",
      reason: plain ? (body!.reason as string) : FAILED,
    };
  } catch {
    return { ok: false, kind: "refused", reason: FAILED };
  }
}

let availability: Promise<{ available: boolean; reason: string | null }> | null = null;
/** Whether this Mac can approve with Touch ID; asked once per Desk session, without a prompt. */
export function readTouchIdAvailability(): Promise<{ available: boolean; reason: string | null }> {
  availability ??= fetchPrimary("/api/neucleos/commerce/subscription-approve/availability", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
    signal: AbortSignal.timeout(STATUS_TIMEOUT_MS),
  })
    .then(async (response) => {
      const body = (await response.json().catch(() => null)) as {
        ok?: unknown;
        available?: unknown;
        reason?: unknown;
      } | null;
      if (response.ok && body?.ok === true && body.available === true) return { available: true, reason: null };
      return { available: false, reason: typeof body?.reason === "string" ? body.reason : UNKNOWN_AVAILABILITY };
    })
    .catch(() => {
      availability = null;
      return { available: false, reason: UNKNOWN_AVAILABILITY };
    });
  return availability;
}
/** Tests only. */
export function resetTouchIdAvailabilityForTest(): void {
  availability = null;
}

export interface TouchIdApprovalState {
  /** Why Approve is unavailable on this Mac (no Touch ID, no helper), or null; "checking" while asked. */
  readonly unavailable: string | null;
  readonly checking: boolean;
  readonly pending: boolean;
  /** The plain words of the last attempt that did not land ("Approval needs your Touch ID."). */
  readonly message: string | null;
  readonly approve: () => Promise<boolean>;
}

/** The card's Touch ID approval: availability, the attempt in flight, and its plain outcome. */
export function useTouchIdApproval(
  enabled: boolean,
  worldKey: string | null,
  gateInstanceKey: string | null,
): TouchIdApprovalState {
  const [status, setStatus] = useState<{ available: boolean; reason: string | null } | null>(null);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void readTouchIdAvailability().then((value) => {
      if (live) setStatus(value);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  useEffect(() => setMessage(null), [gateInstanceKey]);
  const approve = useCallback(async () => {
    if (!enabled || worldKey === null || gateInstanceKey === null) return false;
    setPending(true);
    setMessage(null);
    const outcome = await approveSubscriptionWithTouchId(worldKey, gateInstanceKey);
    setPending(false);
    if (outcome.ok) return true;
    setMessage(outcome.reason);
    if (outcome.kind === "touch_id_unavailable") setStatus({ available: false, reason: outcome.reason });
    return false;
  }, [enabled, worldKey, gateInstanceKey]);
  return {
    unavailable: !enabled || status === null || status.available ? null : (status.reason ?? UNKNOWN_AVAILABILITY),
    checking: enabled && status === null,
    pending,
    message,
    approve,
  };
}
